'use client'

import { useCustomDataPageStore } from '@repo/store/useCustomDataPageStore'
import { useState, useEffect } from 'react'
import PropTypes from 'prop-types'
import { WizardPanel } from '@repo/ui/WizardPanel'
import MUIIconButton from '@mui/material/IconButton'
import MUIStack from '@mui/material/Stack'
import MUISelect from '@mui/material/Select'
import MUIMenuItem from '@mui/material/MenuItem'
import MUIInputLabel from '@mui/material/InputLabel'
import { AddIcon } from '@repo/ui/AddIcon'
import { PaginatedSearchInput } from '@repo/ui/PaginatedSearchInput'
import { WizardFormInput } from '@repo/ui/WizardFormInput'
import { useDebounce } from '@uidotdev/usehooks'

export const CustomDataPageSelectionRow = ({
  comparison,
  removeClause,
  onSelection,
  isLast = false,
  comparisonOperatorDefault
}) => {
  const { fetchSearchCustomDataPages, fetchSearchCustomDataPageFields } = useCustomDataPageStore()

  const { sourceDefault, fieldDefault, ruleDefault, valueDefault } = comparison || {}

  const [source, setSource] = useState(sourceDefault || null)
  const [field, setField] = useState(fieldDefault || null)
  const [rule, setRule] = useState(ruleDefault || null)
  const [value, setValue] = useState(valueDefault || '')
  const debouncedValue = useDebounce(value, 300)
  const [comparisonOperator, setComparisonOperator] = useState(comparisonOperatorDefault || 0)

  const handleSourceChange = (value) => {
    setSource(value)
    setField(null)
    setRule(null)
    setValue('')
  }

  const handleFieldChange = (value) => {
    setField(value)
    setRule(null)
    setValue('')
  }

  useEffect(() => {
    if (onSelection && source && field && rule && debouncedValue) {
      const { customDataPageId } = source || null
      const { customDataFieldId } = field || null

      const updatedComparison = {
        ...comparison,
        customDataPageId,
        customDataFieldId,
        comparisonOperator: rule?.value || 0,
        value: debouncedValue || null,
        sourceDefault: source,
        fieldDefault: field,
        ruleDefault: rule,
        valueDefault: debouncedValue
      }

      onSelection(updatedComparison, comparisonOperator)
    }
  }, [debouncedValue, comparison, field, onSelection, rule, source, comparisonOperator])

  const loadSourceOptions = async (term, loadedOptions, { page }) => {
    const pageSize = 20
    const startAtIndex = (page - 1) * pageSize
    const { rows, totalRowCount } = await fetchSearchCustomDataPages({ term, startAtIndex, pageSize })

    return {
      options: rows,
      hasMore: totalRowCount > startAtIndex + pageSize,
      additional: { page: page + 1 }
    }
  }

  const loadFieldOptions = async (term) => {
    if (!source?.customDataPageId) return { options: [], hasMore: false }
    const options = await fetchSearchCustomDataPageFields({ customDataPageId: source.customDataPageId })
    const filteredOptions = options
      .map((option) => ({ ...option, label: option.description, value: option.customDataFieldId }))
      .filter((option) => option.description.toLowerCase().includes(term.toLowerCase()))

    return {
      options: filteredOptions,
      hasMore: false
    }
  }

  const loadRuleOptions = async () => {
    if (!field?.type) return { options: [], hasMore: false }

    switch (field.type) {
      case 'Text':
      case 'Multiline':
      case 'Address':
      case 'List':
        return {
          options: [
            { label: 'Is equal to', value: 0 },
            { label: 'Contains', value: 4 }
          ],
          hasMore: false
        }
      case 'Number':
        return {
          options: [
            { label: 'Is equal to', value: 0 },
            { label: 'Is not equal to', value: 1 },
            { label: 'Greater than', value: 2 },
            { label: 'Less than', value: 3 }
          ],
          hasMore: false
        }
      case 'Date':
        return {
          options: [
            { label: 'Is equal to', value: 0 },
            { label: 'Is not equal to', value: 1 },
            { label: 'After', value: 2 },
            { label: 'Before', value: 3 }
          ],
          hasMore: false
        }
      default:
        return {
          options: [
            { label: 'Is equal to', value: 0 },
            { label: 'Is not equal to', value: 1 },
            { label: 'Greater than', value: 2 },
            { label: 'Less than', value: 3 },
            { label: 'Contains', value: 4 }
          ],
          hasMore: false
        }
    }
  }

  return (
    <WizardPanel
      data-testid="CustomDataPageSelectionRow"
      sx={{
        minWidth: '100%',
        width: '100%',
        padding: '30px 30px 30px 24px',
        position: 'relative',
        overflow: 'visible'
      }}
    >
      <MUIStack direction="row" gap={4} alignItems="center" sx={{ width: '100%', maxWidth: '100%' }}>
        <PaginatedSearchInput
          label="Custom Data Page"
          onChange={handleSourceChange}
          value={source}
          loadOptions={loadSourceOptions}
        />
        <PaginatedSearchInput
          label="Field"
          onChange={handleFieldChange}
          value={field}
          loadOptions={loadFieldOptions}
          disabled={!source}
          cacheUniqs={[source?.customDataPageId]}
        />
        <PaginatedSearchInput
          label="Rule"
          onChange={setRule}
          value={rule}
          disabled={!field}
          loadOptions={loadRuleOptions}
          searchable={false}
          cacheUniqs={[field?.customDataFieldId]}
        />

        <WizardFormInput
          type="text"
          onChange={setValue}
          value={value}
          label="Value"
          disabled={!rule}
          sx={{ flex: 1, '.MuiInputBase-root': { mb: 0 } }}
        />
      </MUIStack>

      <MUIIconButton
        sx={{
          position: 'absolute',
          right: '0px',
          top: '50%',
          transform: 'translate(50%, -50%) rotate(45deg)',
          backgroundColor: 'background.default',
          zIndex: 1
        }}
        onClick={() => removeClause(comparison.id)}
        title="Remove"
      >
        <AddIcon sx={{ width: '24px', height: '24px' }} />
      </MUIIconButton>

      {!isLast ? (
        <>
          <MUIInputLabel sx={{ display: 'none' }} id="comparisonSelectLabel">
            Age
          </MUIInputLabel>
          <MUISelect
            aria-label="Comparison operator"
            title="Comparison operator"
            value={comparisonOperator}
            labelId="comparisonSelectLabel"
            onChange={(e) => setComparisonOperator(e.target.value)}
            sx={{
              position: 'absolute',
              left: '50%',
              bottom: '0',
              width: '72px',
              height: '32px',
              fontSize: '12px',
              fontWeight: '600',
              padding: '4px',
              transform: 'translate(-50%, 22px)',
              backgroundColor: 'secondary.main',
              borderRadius: '100px',
              color: 'white',
              zIndex: 1,
              cursor: 'pointer',
              mb: 0,
              '.MuiSelect-select': {
                paddingLeft: '6px',
                paddingRight: '28px !important',
                textAlign: 'center'
              },
              fieldset: { border: 'none !important' },
              svg: {
                color: 'white',
                opacity: 0.7
              }
            }}
          >
            <MUIMenuItem value={0}>AND</MUIMenuItem>
            <MUIMenuItem value={1}>OR</MUIMenuItem>
          </MUISelect>
        </>
      ) : null}
    </WizardPanel>
  )
}

CustomDataPageSelectionRow.propTypes = {
  comparison: PropTypes.object,
  removeClause: PropTypes.func,
  onSelection: PropTypes.func,
  isLast: PropTypes.bool,
  comparisonOperatorDefault: PropTypes.number
}
